import { useEffect, useState } from "react";
import { CheckCircle, Warning, CreditCard, Plug, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { fetchBillingConfig } from "@/lib/admin/billingActions";

/**
 * Operational visibility for the billing pipeline. Reads the FastAPI
 * /api/admin/billing-config endpoint, which never returns secrets — only
 * readiness booleans + the last successful Stripe webhook timestamp.
 *
 * We deliberately avoid the word "Connect" because the actual concept
 * is "configured / not configured" with mode + webhook + email signals.
 */
export default function BillingConfigCard() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await fetchBillingConfig();
        if (alive) setConfig(c);
      } catch {
        // Keep implementation details (404s, proxy errors) out of the
        // product surface. The card is operational guidance, not a debug
        // console, and invoice records remain usable when this probe fails.
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [retryToken]);

  if (loading) {
    return (
      <div
        data-testid="admin-billing-config-loading"
        className="rounded-2xl border border-neutral-200 bg-white p-5 text-sm text-neutral-500"
      >
        Loading billing config…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="admin-billing-config-error"
        role="alert"
        className="rounded-2xl border border-amber-300 bg-amber-50/60 p-5 text-sm text-amber-900"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight text-amber-950">
              Billing service status is unavailable
            </h2>
            <p className="mt-1 max-w-2xl leading-relaxed text-amber-900">
              Invoice records are still available. Autopay and invoice email
              status will appear here when the billing service is configured.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-billing-config-retry"
            onClick={() => {
              setError(null);
              setLoading(true);
              setRetryToken((token) => token + 1);
            }}
            className="rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const stripeOk = config.stripe_mode !== "unset";
  const items = [
    {
      key: "stripe",
      icon: CreditCard,
      label: "Stripe",
      ok: stripeOk,
      detail: stripeOk
        ? `Configured · ${config.stripe_mode.toUpperCase()} mode`
        : "Not configured — set STRIPE_API_KEY in backend env",
      tone: stripeOk
        ? config.stripe_mode === "live"
          ? "border-primary/30 bg-primary/5 text-primary-hover"
          : "border-amber-300 bg-amber-50 text-amber-900"
        : "border-neutral-300 bg-neutral-100 text-neutral-700",
    },
    {
      key: "webhook",
      icon: Plug,
      label: "Webhook",
      ok: config.stripe_webhook_secret,
      detail: config.stripe_webhook_secret
        ? "Webhook secret configured"
        : "Not configured — set STRIPE_WEBHOOK_SECRET",
    },
    {
      key: "resend",
      icon: EnvelopeSimple,
      label: "Email-invoice",
      ok: config.resend,
      detail: config.resend
        ? "Ready (Resend configured)"
        : "Not configured — set RESEND_API_KEY to enable email-invoice",
    },
  ];

  return (
    <section
      data-testid="admin-billing-config-card"
      className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm"
    >
      <header className="mb-4">
        <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
          Billing pipeline
        </h2>
        <p className="mt-1 text-xs text-neutral-500">
          Read-only operational status — no secrets are exposed here.
        </p>
      </header>
      <ul className="space-y-2.5">
        {items.map((it) => {
          const Icon = it.icon;
          const tone =
            it.tone ||
            (it.ok
              ? "border-primary/30 bg-primary/5 text-primary-hover"
              : "border-neutral-300 bg-neutral-100 text-neutral-700");
          return (
            <li
              key={it.key}
              data-testid={`admin-billing-config-${it.key}`}
              data-ok={it.ok ? "true" : "false"}
              className={`flex items-start gap-3 rounded-2xl border px-3 py-2.5 ${tone}`}
            >
              <Icon size={18} weight="duotone" className="mt-0.5" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {it.label}{" "}
                  {it.ok ? (
                    <CheckCircle size={13} weight="fill" className="ml-1 inline align-text-top text-primary" />
                  ) : (
                    <Warning size={13} weight="fill" className="ml-1 inline align-text-top text-amber-600" />
                  )}
                </p>
                <p className="text-[12px] leading-relaxed opacity-90">{it.detail}</p>
              </div>
            </li>
          );
        })}
      </ul>
      <footer
        data-testid="admin-billing-config-last-webhook"
        className="mt-4 flex items-center gap-2 text-[12px] text-neutral-500"
      >
        <Clock size={13} weight="duotone" aria-hidden />
        {config.last_webhook_at
          ? `Last successful webhook: ${new Date(config.last_webhook_at).toLocaleString()}`
          : "No successful webhooks recorded yet"}
      </footer>
    </section>
  );
}
